import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Inbox,
    Users,
    BookOpen,
    Award,
    Briefcase,
    FileText,
    LogOut,
    Menu,
    X,
    ShieldCheck,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/leads", label: "Leads", icon: Inbox },
    { to: "/admin/trainees", label: "Trainees", icon: Users },
    { to: "/admin/courses", label: "Courses", icon: BookOpen },
    { to: "/admin/certificates", label: "Certificates", icon: Award },
    { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
    { to: "/admin/applications", label: "Applications", icon: FileText },
];

export default function AdminLayout({ children }) {
    const { admin, logout } = useAdminAuth();
    const navigate = useNavigate();
    const [openMobile, setOpenMobile] = useState(false);

    const handleLogout = () => {
        navigate("/admin/login", { replace: true });
        setTimeout(() => logout(), 0);
    };

    return (
        <div
            className="flex min-h-screen bg-brand-charcoal text-brand-offwhite"
            data-testid="admin-layout"
        >
            {/* Sidebar (desktop) */}
            <aside
                className="hidden w-64 shrink-0 flex-col border-r border-white/5 bg-black/40 md:flex"
                data-testid="admin-sidebar"
            >
                <div className="px-6 pb-6 pt-8">
                    <Link
                        to="/admin"
                        className="flex flex-col items-start gap-2"
                        data-testid="admin-logo"
                        aria-label="EB Construction admin home"
                    >
                        <img
                            src="/brand/logo-horizontal-dark.png?v=3"
                            alt="EB Construction"
                            className="h-9 w-auto"
                        />
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-brand-gold">
                            Admin Console
                        </span>
                    </Link>
                </div>
                <nav className="flex-1 space-y-1 px-3 pb-6" data-testid="admin-nav">
                    {NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 border-l-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                        isActive
                                            ? "border-brand-gold bg-brand-gold/10 text-brand-gold"
                                            : "border-transparent text-brand-offwhite/70 hover:border-brand-gold/40 hover:bg-white/[0.03] hover:text-brand-offwhite"
                                    }`
                                }
                                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                            >
                                <Icon className="h-4 w-4" />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="border-t border-white/5 p-4">
                    <div className="mb-3 flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-brand-gold" />
                        <div className="min-w-0">
                            <p className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite">
                                {admin?.username || "admin"}
                            </p>
                            <p className="truncate font-mono text-[9px] text-brand-offwhite/50">
                                {admin?.email}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center justify-between border border-brand-offwhite/20 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold"
                        data-testid="admin-logout-button"
                    >
                        Log out
                        <LogOut className="h-4 w-4" />
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="flex flex-1 flex-col">
                <header
                    className="flex items-center justify-between border-b border-white/5 bg-black/40 px-5 py-3 md:hidden"
                    data-testid="admin-mobile-topbar"
                >
                    <Link to="/admin" className="flex items-center gap-2">
                        <img
                            src="/brand/logo-monogram.png?v=3"
                            alt="EB Construction"
                            className="h-7 w-7"
                        />
                        <span className="font-display text-base tracking-wide">
                            ADMIN
                        </span>
                    </Link>
                    <button
                        type="button"
                        onClick={() => setOpenMobile((v) => !v)}
                        aria-label="Toggle admin menu"
                        className="grid h-9 w-9 place-items-center border border-white/20"
                        data-testid="admin-mobile-menu-toggle"
                    >
                        {openMobile ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    </button>
                </header>
                {openMobile && (
                    <div
                        className="border-b border-white/5 bg-black/70 md:hidden"
                        data-testid="admin-mobile-menu"
                    >
                        <ul className="flex flex-col p-2">
                            {NAV.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <li key={item.to}>
                                        <NavLink
                                            to={item.to}
                                            end={item.end}
                                            onClick={() => setOpenMobile(false)}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] ${
                                                    isActive
                                                        ? "bg-brand-gold/10 text-brand-gold"
                                                        : "text-brand-offwhite/80"
                                                }`
                                            }
                                            data-testid={`admin-mobile-nav-${item.label.toLowerCase()}`}
                                        >
                                            <Icon className="h-4 w-4" />
                                            {item.label}
                                        </NavLink>
                                    </li>
                                );
                            })}
                            <li className="mt-2 border-t border-white/10">
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    className="flex w-full items-center justify-between px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] text-brand-gold"
                                    data-testid="admin-mobile-logout"
                                >
                                    Log out
                                    <LogOut className="h-4 w-4" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}

                <main className="flex-1 p-6 md:p-10" data-testid="admin-content">
                    {children}
                </main>
            </div>
        </div>
    );
}
